import { CSSProperties, useCallback, useEffect, useMemo, useRef } from "react";
import colorMap from "colormap";
import { debounce } from "lodash";
import styles from "./ThermalImage.module.less";
import {
  convertFahrenheitToUserPreference,
  getUnit,
} from "~/helpers/userPreference";

import {
  INFRARED_MAX_VALUE,
  INFRARED_MIN_VALUE,
} from "../../constants/ladleSensor";
import { ProcessedThermalImages } from "../../types/getProcessedThermalImages";
import ThermalImageWorker, {
  ThermalImageWorkerTypes,
} from "../../workers/thermalImageWorker";
import WorkerFactory from "~/workers/workerFactory";
import { UNITS } from "~/constants/unit";

export interface ISectionMaxValues {
  [key: string]: { x: number; y: number; val: number };
}

const canvasSizeStyles = {
  maxWidth: "100%",
  height: "100%",
};

export interface SectionZoneBoundaryModel {
  ZoneName: string;
  Clock: number;
  StartPoint: {
    X: number;
    Y: number;
  };
  EndPoint: {
    X: number;
    Y: number;
  };
}

const ThermalImage = ({
  data,
  applyFactor,
  onLoadMaxTempValue,
  concerningTemp,
}: {
  data: ProcessedThermalImages | undefined;
  style?: CSSProperties;
  applyFactor?: boolean;
  onLoadMaxTempValue: (maxTempValue: number) => void;
  concerningTemp?: {
    x: number;
    y: number;
    temp: number;
  };
}) => {
  const canvasContainerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const legendsRef = useRef<HTMLCanvasElement | null>(null);
  const highlightCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const toolTipRef = useRef<HTMLDivElement>(null);

  const workerInstance = useMemo(() => WorkerFactory(ThermalImageWorker), []);

  const { temperatureMap, zoneBoundaryInfo } = useMemo(() => {
    return {
      // dataMap: data?.NormalizedImageData ?? [],
      temperatureMap: data?.OutlinedImageData ?? [],
      zoneBoundaryInfo: data?.ZoneBoundaryInfo ?? {
        Height: 0,
        Width: 0,
        HorizontalLines: [],
        VerticalLines: [],
      },
    };
  }, [data]);

  const appliedFactor = useMemo(() => {
    const factor = data?.thermalCalibration.factor ?? 1;
    return applyFactor ? factor : 1;
  }, [data, applyFactor]);

  const colors = useMemo(() => {
    return colorMap({
      colormap: "jet",
    });
  }, []);

  const hotspots = useMemo(() => {
    return data?.Hotspots ?? [];
  }, [data]);

  const transferCanvas = useCallback(() => {
    if (
      canvasRef.current?.transferControlToOffscreen &&
      legendsRef.current?.transferControlToOffscreen &&
      highlightCanvasRef.current?.transferControlToOffscreen
    ) {
      const offscreenCanvasMain =
        canvasRef.current.transferControlToOffscreen();
      const offscreenCanvasLegends =
        legendsRef.current.transferControlToOffscreen();
      const offscreenCanvasHighlight =
        highlightCanvasRef.current.transferControlToOffscreen();

      workerInstance.postMessage(
        {
          type: ThermalImageWorkerTypes.TRANSFER_CANVAS,
          payload: {
            offscreenCanvasMain,
            offscreenCanvasLegends,
            offscreenCanvasHighlight,
            temperatureMap,
            appliedFactor,
            infraredMinValue: INFRARED_MIN_VALUE,
            infraredMaxValue: INFRARED_MAX_VALUE,
            colors,
            zoneBoundaryInfo,
            hotspots,
          },
        },
        [offscreenCanvasMain, offscreenCanvasLegends, offscreenCanvasHighlight],
      );
    }
  }, [
    appliedFactor,
    colors,
    temperatureMap,
    workerInstance,
    zoneBoundaryInfo,
    hotspots,
  ]);

  const generateImage = useCallback(() => {
    workerInstance.postMessage({
      type: ThermalImageWorkerTypes.GENERATE_IMAGE,
      payload: {
        concerningTemp: concerningTemp,
      },
    });
  }, [workerInstance, concerningTemp]);

  const handleMouseHover = useCallback(
    (event: MouseEvent) => {
      const canvasEl = document.getElementById("mainCanvas" + data?.Id);
      if (canvasEl) {
        const mainCanvasRect = canvasEl.getBoundingClientRect();
        const eventClientX = event.clientX;
        const eventClientY = event.clientY;
        const computedStyle = getComputedStyle(canvasEl);

        workerInstance.postMessage({
          type: ThermalImageWorkerTypes.HANDLE_MOUSE_HOVER,
          payload: {
            mainCanvasRect,
            eventClientX,
            eventClientY,
            computedStyleWidth: computedStyle.width,
            computedStyleHeight: computedStyle.height,
          },
        });
      }
    },
    [workerInstance, data?.Id],
  );

  const setTooltipContext = useCallback(
    (positionX: number, positionY: number, thermalValue: number) => {
      if (!positionX || !positionY || !thermalValue || !toolTipRef.current)
        return;

      toolTipRef.current.style.left = `${positionX}px`;
      toolTipRef.current.style.top = `${positionY}px`;
      // We're updating DOM directly because using state for the textContent will significantly decrease performance
      const temperatureInUserPreference = convertFahrenheitToUserPreference(
        Number(thermalValue) * appliedFactor,
      );
      toolTipRef.current.textContent = `${Math.round(
        temperatureInUserPreference,
      )}${getUnit(UNITS.CELSIUS)}`;
    },
    [appliedFactor],
  );

  const handleResize = debounce(() => {
    if (!temperatureMap || !canvasRef.current) {
      return;
    }

    generateImage();
  }, 500);

  /**
   * @deprecated Product Backlog Item 13227: Remove zone lines for IR scans in Copilot
   */
  // const clearHighlightedZone = useCallback(() => {
  //   workerInstance.postMessage({
  //     type: ThermalImageWorkerTypes.CLEAR_HIGHLIGHTED_ZONE,
  //   });
  // }, [workerInstance]);

  const clearTooltip = useCallback(() => {
    if (toolTipRef.current) toolTipRef.current.textContent = "";
  }, []);

  const clearTooltipAndHighlightedZone = useCallback(() => {
    // COMMENT: Product Backlog Item 13227: Remove zone lines for IR scans in Copilot
    // clearHighlightedZone();
    clearTooltip();
  }, [clearTooltip]);

  useEffect(() => {
    const canvasContainer = document.getElementById(
      `canvasContainer${data?.Id}`,
    );
    const tooltipContainer = toolTipRef.current;

    if (canvasContainer && tooltipContainer) {
      canvasContainer.addEventListener("mousemove", handleMouseHover);

      tooltipContainer.addEventListener(
        "mousemove",
        clearTooltipAndHighlightedZone,
      );
      canvasContainer.addEventListener(
        "mouseout",
        clearTooltipAndHighlightedZone,
      );

      window.addEventListener("resize", handleResize);

      return () => {
        canvasContainer.removeEventListener("mousemove", handleMouseHover);

        canvasContainer.removeEventListener(
          "mouseout",
          clearTooltipAndHighlightedZone,
        );
        tooltipContainer.removeEventListener(
          "mousemove",
          clearTooltipAndHighlightedZone,
        );

        window.removeEventListener("resize", handleResize);
      };
    } else {
      return () => {};
    }
  }, [
    clearTooltipAndHighlightedZone,
    generateImage,
    handleMouseHover,
    handleResize,
    canvasRef,
    data?.Id,
  ]);

  useEffect(() => {
    if (!workerInstance) {
      return;
    }

    workerInstance.onmessage = (e) => {
      const { type, payload } = e.data;

      if (type === ThermalImageWorkerTypes.SET_TOOLTIP) {
        const { positionX, positionY, thermalValue } = payload;
        setTooltipContext(positionX, positionY, thermalValue);
      }

      if (type === ThermalImageWorkerTypes.CLEAR_TOOLTIP) {
        clearTooltip();
      }

      if (type === ThermalImageWorkerTypes.MAX_VALUES) {
        onLoadMaxTempValue(
          Math.max(
            ...Object.keys(payload ?? {}).map((key) => payload[key].val || 0),
          ),
        );
      }
    };
    return () => {
      workerInstance.terminate();
    };
  }, [workerInstance, setTooltipContext, clearTooltip, onLoadMaxTempValue]);

  useEffect(() => {
    transferCanvas();
    // console.time("benchmark_time_thermal_image");
    generateImage();
    // console.timeEnd("benchmark_time_thermal_image");

    // COMMENT: yusuf, stop error when save, clear because canvas has changed
    return () => {
      canvasRef.current = null;
      legendsRef.current = null;
      highlightCanvasRef.current = null;
    };
  }, [generateImage, transferCanvas]);

  return (
    <div className={styles.ThermalImage}>
      <div
        id={`canvasContainer${data?.Id}`}
        ref={canvasContainerRef}
        className={styles.canvasContainer}
      >
        <canvas
          id={`mainCanvas${data?.Id}`}
          key={"thermal_" + data?.Id}
          ref={canvasRef}
          className={styles.thermalImageCanvas}
          style={canvasSizeStyles}
        />

        <canvas
          id={"thermalImageLegends_" + data?.Id}
          key={"legends_" + data?.Id}
          ref={legendsRef}
          className={`${styles.thermalImageCanvas} `}
          style={canvasSizeStyles}
        />

        <canvas
          key="highlight"
          ref={highlightCanvasRef}
          className={styles.thermalImageCanvas}
          style={canvasSizeStyles}
        />
      </div>

      <div className={styles.toolTip} ref={toolTipRef} />
    </div>
  );
};

export default ThermalImage;
